import { Link } from 'react-router-dom'
import { useMetro } from '../context/MetroContext'
import { CityId } from '../types/metro.types'

// Smart card data for each metro system
interface SmartCardInfo {
  cardName: string;
  cardCost: string;
  minRecharge: string;
  maxBalance: string;
  discount: string;
  touristCard?: {
    oneDay?: string;
    threeDay?: string;
  };
  officialWebsite: string;
  rechargeOptions: {
    name: string;
    description: string;
    url?: string;
    icon: string;
  }[];
}

const SMART_CARD_DATA: Partial<Record<CityId, SmartCardInfo>> = {
  delhi: {
    cardName: 'Delhi Metro Smart Card',
    cardCost: '50 (refundable)',
    minRecharge: '100',
    maxBalance: '3,000',
    discount: '10%',
    touristCard: {
      oneDay: '200',
      threeDay: '500',
    },
    officialWebsite: 'https://www.delhimetrorail.com',
    rechargeOptions: [
      { name: 'DMRC Official Website', description: 'Recharge via DMRC official portal', url: 'https://www.delhimetrorail.com/e-recharge', icon: '🌐' },
      { name: 'Paytm', description: 'Recharge via Paytm app', url: 'https://paytm.com/delhi-metro-card-recharge', icon: '📱' },
      { name: 'PhonePe', description: 'Recharge via PhonePe app', icon: '📱' },
      { name: 'Metro Station', description: 'Recharge at any metro station counter or ATVM', icon: '🏪' },
    ],
  },
  mumbai: {
    cardName: 'Mumbai Metro One Smart Card',
    cardCost: '50 (refundable)',
    minRecharge: '100',
    maxBalance: '3,000',
    discount: '10%',
    officialWebsite: 'https://www.reliancemumbaimetro.com',
    rechargeOptions: [
      { name: 'Official Website', description: 'Recharge via Mumbai Metro portal', url: 'https://www.reliancemumbaimetro.com', icon: '🌐' },
      { name: 'Paytm', description: 'Recharge via Paytm app', icon: '📱' },
      { name: 'Metro Station', description: 'Recharge at metro station counters', icon: '🏪' },
    ],
  },
  bangalore: {
    cardName: 'Namma Metro Smart Card',
    cardCost: '50 (refundable)',
    minRecharge: '100',
    maxBalance: '3,000',
    discount: '15%',
    touristCard: {
      oneDay: '100',
    },
    officialWebsite: 'https://www.bmrc.co.in',
    rechargeOptions: [
      { name: 'BMRCL Website', description: 'Recharge via official portal', url: 'https://www.bmrc.co.in', icon: '🌐' },
      { name: 'Paytm', description: 'Recharge via Paytm app', icon: '📱' },
      { name: 'WhatsApp', description: 'Recharge via WhatsApp Pay', icon: '💬' },
      { name: 'Metro Station', description: 'Recharge at any metro station', icon: '🏪' },
    ],
  },
  chennai: {
    cardName: 'Chennai Metro Travel Card',
    cardCost: '50 (refundable)',
    minRecharge: '100',
    maxBalance: '3,000',
    discount: '7%',
    officialWebsite: 'https://chennaimetrorail.org',
    rechargeOptions: [
      { name: 'CMRL Website', description: 'Recharge via Chennai Metro portal', url: 'https://chennaimetrorail.org', icon: '🌐' },
      { name: 'Paytm', description: 'Recharge via Paytm app', icon: '📱' },
      { name: 'Metro Station', description: 'Recharge at metro station counters', icon: '🏪' },
    ],
  },
  kolkata: {
    cardName: 'Kolkata Metro Smart Card',
    cardCost: '100 (non-refundable)',
    minRecharge: '100',
    maxBalance: '3,000',
    discount: '10%',
    officialWebsite: 'https://mtp.indianrailways.gov.in',
    rechargeOptions: [
      { name: 'Official Website', description: 'Recharge via Metro Railway portal', icon: '🌐' },
      { name: 'Paytm', description: 'Recharge via Paytm app', icon: '📱' },
      { name: 'Metro Station', description: 'Recharge at metro station counters', icon: '🏪' },
    ],
  },
  hyderabad: {
    cardName: 'L&T Metro Smart Card',
    cardCost: '50 (refundable)',
    minRecharge: '100',
    maxBalance: '2,000',
    discount: '10%',
    touristCard: {
      oneDay: '80',
      threeDay: '220',
    },
    officialWebsite: 'https://www.ltmetro.com',
    rechargeOptions: [
      { name: 'L&T Metro Website', description: 'Recharge via official portal', url: 'https://www.ltmetro.com', icon: '🌐' },
      { name: 'Paytm', description: 'Recharge via Paytm app', icon: '📱' },
      { name: 'T-Savari App', description: 'Official L&T Metro app', icon: '📱' },
      { name: 'Metro Station', description: 'Recharge at any metro station', icon: '🏪' },
    ],
  },
  kochi: {
    cardName: 'Kochi Metro Card',
    cardCost: '50 (refundable)',
    minRecharge: '100',
    maxBalance: '3,000',
    discount: '10%',
    officialWebsite: 'https://kochimetro.org',
    rechargeOptions: [
      { name: 'KMRL Website', description: 'Recharge via Kochi Metro portal', url: 'https://kochimetro.org', icon: '🌐' },
      { name: 'Kochi1 App', description: 'Official Kochi Metro app', icon: '📱' },
      { name: 'Metro Station', description: 'Recharge at any metro station', icon: '🏪' },
    ],
  },
  jaipur: {
    cardName: 'Jaipur Metro Smart Card',
    cardCost: '100 (50 refundable)',
    minRecharge: '100',
    maxBalance: '3,000',
    discount: '10%',
    officialWebsite: 'https://jaipurmetrorail.in',
    rechargeOptions: [
      { name: 'Official Website', description: 'Recharge via Jaipur Metro portal', url: 'https://jaipurmetrorail.in', icon: '🌐' },
      { name: 'Metro Station', description: 'Recharge at any metro station', icon: '🏪' },
    ],
  },
  lucknow: {
    cardName: 'Go Smart Card (Lucknow Metro)',
    cardCost: '100 (refundable)',
    minRecharge: '100',
    maxBalance: '3,000',
    discount: '10%',
    officialWebsite: 'https://lmrcl.com',
    rechargeOptions: [
      { name: 'LMRC Website', description: 'Recharge via official portal', url: 'https://lmrcl.com', icon: '🌐' },
      { name: 'Paytm', description: 'Recharge via Paytm app', icon: '📱' },
      { name: 'Metro Station', description: 'Recharge at any metro station', icon: '🏪' },
    ],
  },
  nagpur: {
    cardName: 'Nagpur Metro Card',
    cardCost: '50 (refundable)',
    minRecharge: '100',
    maxBalance: '3,000',
    discount: '10%',
    officialWebsite: 'https://metrorailnagpur.com',
    rechargeOptions: [
      { name: 'Official Website', description: 'Recharge via Nagpur Metro portal', url: 'https://metrorailnagpur.com', icon: '🌐' },
      { name: 'Metro Station', description: 'Recharge at any metro station', icon: '🏪' },
    ],
  },
  ahmedabad: {
    cardName: 'Ahmedabad Metro Card',
    cardCost: '50 (refundable)',
    minRecharge: '100',
    maxBalance: '3,000',
    discount: '10%',
    officialWebsite: 'https://gujaratmetrorail.com',
    rechargeOptions: [
      { name: 'GMRC Website', description: 'Recharge via Gujarat Metro portal', url: 'https://gujaratmetrorail.com', icon: '🌐' },
      { name: 'Paytm', description: 'Recharge via Paytm app', icon: '📱' },
      { name: 'Metro Station', description: 'Recharge at any metro station', icon: '🏪' },
    ],
  },
};

// Default smart card info for cities without specific data
const DEFAULT_SMART_CARD: SmartCardInfo = {
  cardName: 'Metro Smart Card',
  cardCost: '50-100',
  minRecharge: '100',
  maxBalance: '3,000',
  discount: '10%',
  officialWebsite: '',
  rechargeOptions: [
    { name: 'Metro Station', description: 'Recharge at any metro station counter', icon: '🏪' },
    { name: 'Paytm', description: 'Recharge via Paytm app (if available)', icon: '📱' },
  ],
};

function SmartCard() {
  const { selectedCity, currentCity } = useMetro()

  // Get smart card info for selected city
  const smartCardInfo = selectedCity
    ? (SMART_CARD_DATA[selectedCity] || DEFAULT_SMART_CARD)
    : DEFAULT_SMART_CARD

  const hasSpecificData = selectedCity && SMART_CARD_DATA[selectedCity]

  if (!selectedCity) {
    return (
      <div className="py-8 lg:py-12">
        <div className="container-responsive max-w-4xl">
          <div className="card text-center py-12">
            <span className="text-6xl mb-4 block">🏙️</span>
            <h2 className="text-xl font-semibold mb-2">Select a City First</h2>
            <p className="text-neutral-dark/70 mb-4">
              Please select a city to see smart card information
            </p>
            <Link to="/cities" className="btn-primary inline-block">
              Select City
            </Link>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="py-8 lg:py-12">
      <div className="container-responsive max-w-4xl">
        <h1 className="text-3xl lg:text-4xl 2xl:text-tv-2xl font-bold text-center mb-2">
          {smartCardInfo.cardName}
        </h1>
        <p className="text-center text-neutral-dark/70 mb-8">
          Smart card information for {currentCity?.name} Metro
        </p>

        {!hasSpecificData && (
          <div className="card text-center py-4 mb-6 bg-metro-yellow-soft">
            <p className="text-sm text-neutral-dark/70">
              Showing general smart card information. City-specific details coming soon.
            </p>
          </div>
        )}

        {/* Benefits */}
        <div className="card mb-8">
          <h2 className="text-xl font-semibold mb-4">Smart Card Benefits</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-start gap-3 p-4 bg-metro-green-soft rounded-lg">
              <span className="text-2xl">💰</span>
              <div>
                <h3 className="font-medium">Save up to {smartCardInfo.discount}</h3>
                <p className="text-sm text-neutral-dark/70">Discount on every journey compared to token fare</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-4 bg-metro-blue-soft rounded-lg">
              <span className="text-2xl">⚡</span>
              <div>
                <h3 className="font-medium">Faster Entry</h3>
                <p className="text-sm text-neutral-dark/70">No need to queue for tokens at counters</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-4 bg-metro-violet-soft rounded-lg">
              <span className="text-2xl">🔄</span>
              <div>
                <h3 className="font-medium">Easy Recharge</h3>
                <p className="text-sm text-neutral-dark/70">Recharge online or at any metro station</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-4 bg-metro-yellow-soft rounded-lg">
              <span className="text-2xl">📊</span>
              <div>
                <h3 className="font-medium">Track Usage</h3>
                <p className="text-sm text-neutral-dark/70">View journey history and balance</p>
              </div>
            </div>
          </div>
        </div>

        {/* Recharge Options */}
        <div className="card mb-8">
          <h2 className="text-xl font-semibold mb-4">Recharge Options</h2>
          <div className="space-y-4">
            {smartCardInfo.rechargeOptions.map((option, index) => (
              <a
                key={index}
                href={option.url || '#'}
                target={option.url ? '_blank' : undefined}
                rel={option.url ? 'noopener noreferrer' : undefined}
                className={`flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:border-metro-blue hover:bg-metro-blue-soft/30 transition-colors ${
                  !option.url ? 'cursor-default' : ''
                }`}
              >
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{option.icon}</span>
                  <div>
                    <h3 className="font-medium">{option.name}</h3>
                    <p className="text-sm text-neutral-dark/70">{option.description}</p>
                  </div>
                </div>
                {option.url && <span className="text-metro-blue">→</span>}
              </a>
            ))}
          </div>
          {smartCardInfo.officialWebsite && (
            <p className="text-sm text-neutral-dark/60 mt-4">
              Official website:{' '}
              <a
                href={smartCardInfo.officialWebsite}
                target="_blank"
                rel="noopener noreferrer"
                className="text-metro-blue hover:underline"
              >
                {smartCardInfo.officialWebsite}
              </a>
            </p>
          )}
        </div>

        {/* Card Types */}
        <div className="card">
          <h2 className="text-xl font-semibold mb-4">Card Types</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 border-2 border-metro-blue rounded-lg">
              <h3 className="font-semibold text-lg mb-2">Standard Card</h3>
              <ul className="space-y-2 text-sm text-neutral-dark/70">
                <li>• Card cost: ₹{smartCardInfo.cardCost}</li>
                <li>• Minimum recharge: ₹{smartCardInfo.minRecharge}</li>
                <li>• Maximum balance: ₹{smartCardInfo.maxBalance}</li>
                <li>• {smartCardInfo.discount} discount on fares</li>
              </ul>
            </div>
            {smartCardInfo.touristCard && (
              <div className="p-4 border-2 border-metro-violet rounded-lg">
                <h3 className="font-semibold text-lg mb-2">Tourist Card</h3>
                <ul className="space-y-2 text-sm text-neutral-dark/70">
                  {smartCardInfo.touristCard.oneDay && (
                    <li>• 1-day: ₹{smartCardInfo.touristCard.oneDay} (unlimited rides)</li>
                  )}
                  {smartCardInfo.touristCard.threeDay && (
                    <li>• 3-day: ₹{smartCardInfo.touristCard.threeDay} (unlimited rides)</li>
                  )}
                  <li>• Card cost: ₹50 (refundable)</li>
                  <li>• Best for tourists</li>
                </ul>
              </div>
            )}
            {!smartCardInfo.touristCard && (
              <div className="p-4 border-2 border-gray-200 rounded-lg bg-gray-50">
                <h3 className="font-semibold text-lg mb-2 text-neutral-dark/50">Tourist Card</h3>
                <p className="text-sm text-neutral-dark/50">
                  Tourist card information not available for this metro system.
                  Please check the official website for more details.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default SmartCard
